import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import styles from "./chatwindow.module.css";
import ThoughtsDrawer from "../ThoughtsDrawer";
import AgentTimeline, { TimelineStep } from "../AgentTimeline";
import { timelineData } from "../../data/mock";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { ChatMessage, sendUserPreferenceToAgent } from "../../store/userSlice";
import { defaultSteps, getStepsByAgentId } from "../../utils/constants";
import StaticProgressTracker from "../StaticProgressTracker";
import StreamProgressTracker from "../StreamProgressTracker";
import AgentResponse from "../AgentResponse";
import { copyAgentContent } from "../../utils/copyUtils";
import { safeJSONParse, extractFileFromContent } from "../../utils/helper";
import { recordAgentAction } from "../../store/agentSlice";

import {
  Chip,
  Button,
  Typography,
  Box,
  Paper,
  Popover,
  IconButton,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import PsychologyIcon from "@mui/icons-material/Psychology";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Tooltip from "@mui/material/Tooltip";
import MarkdownRenderer from "../MarkdownRenderer";

import jsPDF from "jspdf";
import {
  formatContentForPDF,
  addPDFHeader,
  generateTextPDF,
  hasArabicCharacters,
  generateMixedContentPDF,
  generatePDFFileName,
  getCustomPDFHeader,
} from "../../utils/pdfUtils";
import { Cursor } from "recharts/types/component/Cursor";

export interface ProgressStep {
  id: string;
  title: string;
}

type IconData = {
  id: number;
  paths: string[];
  rectRadius?: number;
};

const progressExcludedAgents = [
  "utility_agent",
  "utility_agent_form",
  "workflow_end",
  "hr_agent_v2_hitl",
  "validation_check",
  "no_candidates_found",
  "rfq_builder",
  "utility_agent_redline_intake",
  "utility_agent_redline_doc_analyzer",
  "utility_agent_redline_doc_analyzer_sow_buttons",
  "utility_agent_redline_doc_analyzer_sow_msa",
  "skill_mapping_agent_modify",
  "skill_mapping_agent_download",
  "cc_outcome",
  "cc_basic_intake",
  "cc_detailed_intake",
  "cc_intake",
  "cc_flow_decision",
  "cc_existing_info",
];

const feedbackExcludedAgents = [
  "utility_agent",
  "utility_agent_form",
  "resume_fetcher_agent",
  "validation_check",
  "no_candidates_found",
  // "template_reviewer_agent", // Removed - now accepts feedback
  "socialmedia_intake_agent",
  "press_release_qa_agent",
  "PressReleaseQAAgent",
  "rfq_builder",
  // "contract_reviewer_orchestrator", // Removed - now accepts feedback
  // "ConsolidatorAgent",
  // "consolidator_agent",
  // "template_reviewer_intake_agent",
  // "template_reviewer_checklist_agent",
  // "template_guided_intake_agent",
  // "template_document_selector_agent",
  // "template_drafter_agent",
  // "template_reviewer_qa_agent",
  // "template_request_orchestrator",
  "utility_agent_redline_intake",
  "utility_agent_redline_doc_analyzer",
  "utility_agent_redline_doc_analyzer_unknown_buttons",
  "utility_agent_redline_doc_analyzer_sow_buttons",
  "utility_agent_redline_doc_analyzer_sow_msa",
  "learning_path_utility_agent_form",
  "training_search_preferences_form",
  "training_search_chat",
  "cc_outcome",
  "cc_basic_intake_error",
  "cc_basic_intake",
  "cc_detailed_intake",
  "cc_intake",
  "cc_flow_decision",
  "cc_existing_info",
  "cc_overwrite_confirmation",
];

const downloadExcludedAgents = [
  "email_drafting",
  "translation_agent",
  "contract_reviewer_orchestrator",
  "ConsolidatorAgent",
  "consolidator_agent",
  "template_reviewer_intake_agent",
  "template_reviewer_checklist_agent",
  "template_guided_intake_agent",
  "template_document_selector_agent",
  "template_drafter_agent",
  "template_reviewer_qa_agent",
  "template_request_orchestrator",
  "template_reviewer_agent",
  "sow_auto_filler",
  "document_analyser_executor",
  "outcome_modifier_executor",
  "outcome_finalizer_executor",
  "redline",
  "skill_mapping_agent",
  "skill_mapping_agent_modify",
  "skill_mapping_agent_download",
  "training_search_agent",
  "learning_path_agent",
  "memorandum-of-understanding",
  "mou_orchestrator",
  "mou_agent",
  "mou_entity_agent",
  "mou_intake_agent",
  "supplier_discovery_requirement_review",
  "supplier_discovery_qna",
  "supplier_discovery_results",
  "interview_intake_response",
  "question_generation_agent",
  "cc_outcome",
  "cc_basic_intake_error",
  "cc_detailed_intake",
  "cc_overwrite_confirmation",
];

const iconsData: IconData[] = [
  {
    id: 0,
    paths: [
      "M9.84862 17.2328H14.9291C15.3928 17.2328 15.7739 16.9493 15.9393 16.5417L17.6259 12.5423C17.6761 12.4123 17.7027 12.2764 17.7027 12.1287V10.9886C17.7027 10.3653 17.2005 9.6712 16.5862 9.6712H13.0653L13.5969 7.26388L13.6147 7.08666C13.6147 6.85331 13.5202 6.64064 13.3695 6.48704L12.7728 5.89038L9.0511 9.65053C8.85025 9.85434 8.72619 10.1379 8.72619 10.451V16.1222C8.72619 16.7455 9.23424 17.2328 9.84862 17.2328ZM5.41797 10.6164H7.30838V17.2328H5.41797V10.6164Z",
    ],
  },
  {
    id: 1,
    paths: [
      "M13.275 5.89038H8.19451C7.73077 5.89038 7.34973 6.17394 7.18432 6.58156L5.49477 10.581C5.44455 10.7109 5.41797 10.8468 5.41797 10.9945V12.1346C5.41797 12.7579 5.92011 13.452 6.53449 13.452H10.0583L9.52666 15.8593L9.50894 16.0366C9.50894 16.2699 9.60346 16.4826 9.7541 16.6362L10.3449 17.2299L14.0725 13.4727C14.2734 13.2689 14.3974 12.9853 14.3974 12.6722V7.001C14.3974 6.37775 13.8894 5.89038 13.275 5.89038ZM15.8152 5.89038H17.7056V12.5068H15.8152V5.89038Z",
    ],
  },
  {
    id: 2,
    paths: [
      "M12 6.75C11.5858 6.75 11.25 7.08579 11.25 7.5V12.1893L9.53033 10.4697C9.23744 10.1768 8.76256 10.1768 8.46967 10.4697C8.17678 10.7626 8.17678 11.2374 8.46967 11.5303L11.4697 14.5303C11.7626 14.8232 12.2374 14.8232 12.5303 14.5303L15.5303 11.5303C15.8232 11.2374 15.8232 10.7626 15.5303 10.4697C15.2374 10.1768 14.7626 10.1768 14.4697 10.4697L12.75 12.1893V7.5C12.75 7.08579 12.4142 6.75 12 6.75Z",
      // Bottom tray / PDF bar
      "M7 15.25C6.58579 15.25 6.25 15.5858 6.25 16C6.25 16.4142 6.58579 16.75 7 16.75H17C17.4142 16.75 17.75 16.4142 17.75 16C17.75 15.5858 17.4142 15.25 17 15.25H7Z",
    ],
  },
  {
    id: 3,
    paths: [
      "M14.3962 7.39686H17.0546C17.1521 7.39686 17.2318 7.31711 17.2318 7.21964C17.2318 6.97743 17.1225 6.74703 16.9364 6.59048L14.6591 4.69416C14.5144 4.57306 14.2397 4.47559 14.0506 4.47559C13.9295 4.47559 13.832 4.57306 13.832 4.69416V6.83565C13.835 7.14579 14.0861 7.39686 14.3962 7.39686Z",
      "M13.0685 6.83567V4.47266H9.19911C8.67925 4.47266 8.25391 4.898 8.25391 5.41786V15.3425C8.25391 15.8624 8.67925 16.2877 9.19911 16.2877H16.2882C16.808 16.2877 17.2334 15.8624 17.2334 15.3425V8.16487H14.3977C13.6652 8.16487 13.0685 7.5682 13.0685 6.83567Z",
      "M7.42658 16.1695V6.36304H6.83583C6.31597 6.36304 5.89062 6.78838 5.89062 7.30824V17.7055C5.89062 18.2254 6.31597 18.6507 6.83583 18.6507H14.3975C14.9173 18.6507 15.3427 18.2254 15.3427 17.7055V17.1147H8.37179C7.85193 17.1147 7.42658 16.6894 7.42658 16.1695Z",
    ],
  },
];

export type Message = {
  id: string;
  sender: "user" | "agent";
  text: JSX.Element | string;
  timestamp?: number;
  backendMessageId?: string; // Backend message_id for feedback
  isPersisted?: boolean; // Whether create API has completed
};

type MessageBubbleProps = {
  // messages: Message[];
  currentChat: ChatMessage[];
  userAvatar?: string;
  agentAvatar?: string;
  sessionId?: string; // Backend session_id for feedback
  showThinkingBubble?: boolean;
  inputRef?: React.RefObject<any>;
  agentId?: string;
};

const MOU_AGENT_IDS = [
  "memorandum-of-understanding",
  "mou_orchestrator",
  "mou_agent",
  "mou_entity_agent",
  "mou_intake_agent",
];

function extractMouDocxUrl(message: string): string | null {
  try {
    const parsed = safeJSONParse(message);
    if (!parsed) return null;
    const finalAnswer = parsed?.finalAnswer || parsed;
    const downloadUrl = parsed?.download_url || finalAnswer?.download_url;
    if (downloadUrl && typeof downloadUrl === "string") return downloadUrl;
    const responseRaw = finalAnswer?.response;
    const responseText = Array.isArray(responseRaw)
      ? responseRaw.join("\n\n")
      : responseRaw || "";
    const match = responseText.match(/\[Download DOCX\]\((https?[^)]+)\)/i);
    return match?.[1] || null;
  } catch {
    return null;
  }
}

function getMouChainOfThought(messageContent: string): TimelineStep[] {
  try {
    const parsed = safeJSONParse(messageContent);
    if (!parsed) return [];
    const finalAnswer = parsed?.finalAnswer || parsed;
    const steps: TimelineStep[] = [];

    const responseRaw = finalAnswer?.response;
    if (Array.isArray(responseRaw)) {
      responseRaw.forEach((item: any) => {
        if (typeof item === "string" && item.trim()) {
          // String response — use first non-empty line (stripped) as title, full markdown as subtitle
          const firstLine =
            item
              .trim()
              .split("\n")
              .find((l: string) => l.trim())
              ?.replace(/\*\*/g, "")
              .replace(/^#+\s*/, "")
              .replace(/📄\s*/g, "")
              .trim() || "Agent Response";
          steps.push({ title: firstLine, subtitle: item.trim() });
        } else if (item?.agent_id && item?.result) {
          // Structured agent result — build readable summary
          const result = item.result;
          const parts: string[] = [];
          if (result.counterparty_name)
            parts.push(`**Counterparty:** ${result.counterparty_name}`);
          if (result.project_name)
            parts.push(`**Project:** ${result.project_name}`);
          if (result.jurisdiction)
            parts.push(`**Jurisdiction:** ${result.jurisdiction}`);
          if (result.collaboration_summary)
            parts.push(`**Collaboration:** ${result.collaboration_summary}`);
          if (result.key_objectives?.length) {
            parts.push(
              `**Key Objectives:**\n${(result.key_objectives as string[]).map((o: string) => `- ${o}`).join("\n")}`,
            );
          }
          const agentLabel = item.agent_id
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c: string) => c.toUpperCase());
          steps.push({
            title: agentLabel,
            subtitle: parts.join("\n\n") || result.message || "Completed",
          });
        }
      });
    } else if (typeof responseRaw === "string" && responseRaw.trim()) {
      steps.push({ title: "Agent Response", subtitle: responseRaw.trim() });
    }

    if (finalAnswer?.question && typeof finalAnswer.question === "string") {
      steps.push({ title: "Next Step", subtitle: finalAnswer.question });
    }

    return steps;
  } catch {
    return [];
  }
}

function MouChainOfThoughtView({ steps }: { steps: TimelineStep[] }) {
  if (!steps.length) {
    return (
      <Box sx={{ p: 2, color: "#6b7280", fontSize: "13px" }}>
        No chain of thought available for this step.
      </Box>
    );
  }
  return (
    <Box sx={{ p: 2 }}>
      <Typography
        sx={{ fontWeight: 600, fontSize: "13px", color: "#004141", mb: 2 }}
      >
        MoU Workflow Steps
      </Typography>
      {steps.map((step, idx) => (
        <Box
          key={idx}
          sx={{
            mb: 2,
            pl: 2,
            borderLeft: "3px solid #b2e5de",
            "&:last-child": { mb: 0 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              color: "#004141",
              mb: step.subtitle ? 0.5 : 0,
            }}
          >
            {step.title}
          </Typography>
          {step.subtitle && (
            <Box sx={{ fontSize: "12px", color: "#374151" }}>
              <MarkdownRenderer content={step.subtitle} />
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

// Helper function to infer executor from SOW message content
function inferSowExecutor(messageContent: string): string | null {
  const parsedContent = safeJSONParse(messageContent);
  if (!parsedContent) return null;

  const finalAnswer = parsedContent?.finalAnswer || parsedContent;
  const status = finalAnswer?.status || finalAnswer?.state;
  const message = finalAnswer?.message || "";

  // Infer executor based on status and message patterns
  if (status === "awaiting_bu_category_confirmation") {
    return "sow_pre_analyzer";
  }

  if (status === "awaiting_contract_name") {
    return "sow_auto_filler";
  }

  if (status === "awaiting_confirmation") {
    // Check if it's a question answer or document analysis
    if (
      message.includes("Answer to your question:") ||
      message.includes("**Answer to your question:**")
    ) {
      return "sow_question_answerer";
    }
    // Otherwise it's the document analyzer showing SOW Auto-Fill Summary
    return "sow_document_analyzer";
  }

  if (status === "completed") {
    return "sow_final_generator";
  }

  // Default to sow_auto_filler for other statuses
  return "sow_auto_filler";
}

export function getAgentSteps(
  agentIdName?: string | null,
  isImageGeneration?: boolean,
  messageContent?: string,
): ProgressStep[] {
  let idToUse = agentIdName || undefined;

  // For SOW agents, infer the specific executor from message content
  if (agentIdName === "sow_auto_filler" && messageContent) {
    const inferredExecutor = inferSowExecutor(messageContent);
    if (inferredExecutor) {
      idToUse = inferredExecutor;
    }
  }

  // For contract reviewer, check if it's a QA agent response
  if (agentIdName === "contract_reviewer_orchestrator" && messageContent) {
    try {
      const parsedContent = safeJSONParse(messageContent);
      // Check if response array contains contract_reviewer_qa_agent
      if (
        parsedContent?.response &&
        Array.isArray(parsedContent.response) &&
        parsedContent.response.some(
          (item: any) => item.agent_id === "contract_reviewer_qa_agent",
        )
      ) {
        console.log("Detected QA agent in message - switching to QA steps");
        idToUse = "contract_reviewer_qa_agent";
      }
    } catch (e) {
      console.warn("Error checking for QA agent in message:", e);
    }
  }

  const steps = getStepsByAgentId(idToUse, isImageGeneration);
  return steps.length > 0 ? steps : defaultSteps;
}

// Helper function to check if message contains a question WITHOUT substantial response
const messageHasQuestionOnly = (messageContent: string): boolean => {
  const parsedContent = safeJSONParse(messageContent);
  if (!parsedContent) return false;

  // Check for question at top level or in finalAnswer
  const question =
    parsedContent.question ||
    parsedContent?.finalAnswer?.question ||
    parsedContent?.result?.question;

  // Only return true if there's a question AND no substantial result/response data
  if (!question || question.trim().length === 0) {
    return false;
  }

  // Check if there's substantial response data alongside the question
  const hasSubstantialData =
    parsedContent.result ||
    parsedContent.response ||
    (parsedContent.finalAnswer &&
      Object.keys(parsedContent.finalAnswer).length > 1);

  // Hide progress only when there's a question but NO substantial data
  return !hasSubstantialData;
};

// Helper function to detect if a message is a Learning Path form submission
const isLearningPathFormData = (message: string): boolean => {
  try {
    const parsed = JSON.parse(message);
    return (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed[0]?.key === "Preferred_Content"
    );
  } catch {
    return false;
  }
};

// Helper function to detect if a message is a Training Search form submission
const isTrainingSearchFormData = (message: string): boolean => {
  try {
    const parsed = JSON.parse(message);
    return (
      Array.isArray(parsed) && parsed.length > 0 && parsed[0]?.key === "Budget"
    );
  } catch {
    return false;
  }
};

// Helper function to detect if a message is a Manpower Cost Calculator form submission
const isManpowerCostFormData = (message: string): boolean => {
  try {
    const parsed = JSON.parse(message);
    return (
      parsed &&
      typeof parsed === "object" &&
      "project_name" in parsed &&
      "serial_number" in parsed &&
      "action" in parsed &&
      parsed.action === "calculate_manpower_cost"
    );
  } catch {
    return false;
  }
};

// Helper function to format Manpower Cost Calculator form data for display
const formatManpowerCostFormData = (message: string): JSX.Element | null => {
  try {
    const parsed = JSON.parse(message);
    if (!parsed || typeof parsed !== "object") return null;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ fontSize: "13px", lineHeight: 1.6 }}>
          <strong>Project Name:</strong> {parsed.project_name || "N/A"}
        </Box>
        <Box sx={{ fontSize: "13px", lineHeight: 1.6 }}>
          <strong>Serial Number:</strong> {parsed.serial_number || "N/A"}
        </Box>
        <Box
          sx={{
            fontSize: "13px",
            lineHeight: 1.6,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <AttachFileIcon sx={{ fontSize: "16px", color: "#666" }} />
          <span>Resource plan template uploaded</span>
        </Box>
      </Box>
    );
  } catch {
    return null;
  }
};

// Helper function to detect if a message is a Document Feedback submission
const isDocumentFeedbackData = (message: string): boolean => {
  try {
    const parsed = JSON.parse(message);
    return (
      parsed &&
      typeof parsed === "object" &&
      parsed.response &&
      Array.isArray(parsed.document_feedback)
    );
  } catch {
    return false;
  }
};

// Helper function to detect if a message is Supplier Discovery confirmation data
const isSupplierDiscoveryData = (message: string): boolean => {
  try {
    const parsed = JSON.parse(message);
    return (
      parsed &&
      typeof parsed === "object" &&
      parsed.agent_id === "supplier_discovery_requirement_review" &&
      parsed.requirement &&
      Array.isArray(parsed.weights)
    );
  } catch {
    return false;
  }
};

// Helper function to format Supplier Discovery confirmation data for display
const formatSupplierDiscoveryData = (message: string): JSX.Element | null => {
  try {
    const parsed = JSON.parse(message);
    if (!parsed || !parsed.requirement) return null;

    const { requirement } = parsed;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "14px", mb: 0.5 }}>
          Supplier Search Confirmed
        </Typography>
        <Box sx={{ fontSize: "13px", lineHeight: 1.6 }}>
          <strong>Category:</strong> {requirement.category}
        </Box>
        <Box sx={{ fontSize: "13px", lineHeight: 1.6 }}>
          <strong>Capabilities:</strong>{" "}
          {Array.isArray(requirement.capabilities)
            ? requirement.capabilities.join(", ")
            : requirement.capabilities}
        </Box>
        <Box sx={{ fontSize: "13px", lineHeight: 1.6 }}>
          <strong>Qualifications:</strong>{" "}
          {Array.isArray(requirement.qualifications)
            ? requirement.qualifications.join(", ")
            : requirement.qualifications}
        </Box>
      </Box>
    );
  } catch {
    return null;
  }
};

// Helper function to format Learning Path form data for display
const formatLearningPathFormData = (message: string): JSX.Element | null => {
  try {
    const parsed = JSON.parse(message);
    if (!Array.isArray(parsed)) return null;

    const formattedData: Record<string, any> = {};
    parsed.forEach((item: any) => {
      if (item.key && item.answer !== undefined) {
        formattedData[item.key] = item.answer;
      } else if (item.key === "Proficiency_Levels" && item.skills) {
        formattedData[item.key] = item.skills;
      }
    });

    const labelMap: Record<string, string> = {
      Preferred_Content: "Preferred Content Format",
      Preferred_Language: "Preferred Language",
      Current_Role: "Current Role",
      Target_Role: "Target Role",
      Target_Timeline: "Target Timeline",
      Relevant_Certifications: "Relevant Certifications",
      Previous_Learning_Paths: "Previous Learning Paths",
      Proficiency_Levels: "Proficiency Levels",
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "14px", mb: 0.5 }}>
          Learning Path Preferences Submitted
        </Typography>
        {Object.entries(formattedData).map(([key, value]) => {
          const label = labelMap[key] || key;

          // Skip empty values
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return null;
          }

          // Handle Proficiency Levels separately
          if (key === "Proficiency_Levels" && Array.isArray(value)) {
            return (
              <Box key={key} sx={{ fontSize: "13px", lineHeight: 1.6 }}>
                <strong>{label}:</strong>
                <Box
                  sx={{
                    mt: 0.5,
                    ml: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.3,
                  }}
                >
                  {value.map((skill: any, idx: number) => (
                    <Box key={idx}>
                      • {skill.skill}: Current Level {skill.current_score},
                      Target Level {skill.target_level}
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          }

          // Format array values (like content formats)
          let displayValue: string;
          if (Array.isArray(value)) {
            displayValue = value.join(", ");
          } else {
            displayValue = String(value);
          }

          return (
            <Box key={key} sx={{ fontSize: "13px", lineHeight: 1.6 }}>
              <strong>{label}:</strong> {displayValue}
            </Box>
          );
        })}
      </Box>
    );
  } catch {
    return null;
  }
};

// Helper function to format Document Feedback data for display
const formatDocumentFeedbackData = (message: string): JSX.Element | null => {
  try {
    const parsed = JSON.parse(message);
    if (!parsed || !parsed.response) return null;

    // Defensive: Ensure arrays and filter out null/undefined items
    const feedbackItems = Array.isArray(parsed.document_feedback)
      ? parsed.document_feedback.filter(
          (item: any) => item && typeof item === "object",
        )
      : [];
    const collaborationItems = Array.isArray(parsed.document_collaboration)
      ? parsed.document_collaboration.filter(
          (item: any) => item && typeof item === "object",
        )
      : [];

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {/* Response */}
        <Typography sx={{ fontSize: "14px" }}>
          {parsed.response || "Response unavailable"}
        </Typography>

        {/* Feedback Section */}
        {feedbackItems.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "13px", mb: 0.5 }}>
              Feedback:
            </Typography>
            {feedbackItems.map((item: any, index: number) => {
              // Defensive: Use optional chaining and fallbacks
              const documentName = item?.document_name || "Unknown Document";
              const feedbackComment = item?.feedback_comment || "No comment";

              return (
                <Box key={index} sx={{ mb: 1, fontSize: "13px" }}>
                  <div>
                    <strong>{documentName}</strong>
                  </div>
                  <div style={{ marginLeft: "8px", color: "#666" }}>
                    {feedbackComment}
                  </div>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Collaboration Section */}
        {collaborationItems.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "13px", mb: 0.5 }}>
              Collaboration:
            </Typography>
            {collaborationItems.map((item: any, index: number) => {
              // Defensive: Use optional chaining and fallbacks
              const documentName = item?.document_name || "Unknown Document";
              const assignedTo = item?.assigned_to;
              const reviewStatus = item?.review_status;
              const reviewerComments = item?.reviewer_comments;
              const verified = item?.verified === true;

              return (
                <Box key={index} sx={{ mb: 1, fontSize: "13px" }}>
                  <div>
                    <strong>{documentName}</strong>
                  </div>
                  <div style={{ marginLeft: "8px", color: "#666" }}>
                    {assignedTo && <div>Assigned to: {assignedTo}</div>}
                    {reviewStatus && <div>Review Status: {reviewStatus}</div>}
                    {reviewerComments && (
                      <div>Comments: {reviewerComments}</div>
                    )}
                    <div>Status: {verified ? "Verified" : "Not Verified"}</div>
                  </div>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  } catch (error) {
    // Log error for debugging but don't crash the UI
    console.error("Error formatting document feedback data:", error);
    return null;
  }
};

// Helper function to format Training Search form data for display
const formatTrainingSearchFormData = (message: string): JSX.Element | null => {
  try {
    const parsed = JSON.parse(message);
    if (!Array.isArray(parsed)) return null;

    const formattedData: Record<string, any> = {};
    parsed.forEach((item: any) => {
      if (item.key && item.answer !== undefined) {
        formattedData[item.key] = item.answer;
      }
    });

    const labelMap: Record<string, string> = {
      Budget: "Budget",
      Timeframe: "Timeframe",
      Mode: "Training Mode",
      Language: "Language",
      Location: "Location",
      Certification_Preference: "Certification Preference",
      Learning_Capacity: "Learning Capacity",
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "14px", mb: 0.5 }}>
          Training Search Preferences Submitted
        </Typography>
        {Object.entries(formattedData).map(([key, value]) => {
          const label = labelMap[key] || key;

          // Skip empty values
          if (
            !value ||
            value === "" ||
            (Array.isArray(value) && value.length === 0)
          ) {
            return null;
          }

          // Format array values (like training modes)
          let displayValue: string;
          if (Array.isArray(value)) {
            displayValue = value.join(", ");
          } else {
            displayValue = String(value);
          }

          return (
            <Box key={key} sx={{ fontSize: "13px", lineHeight: 1.6 }}>
              <strong>{label}:</strong> {displayValue}
            </Box>
          );
        })}
      </Box>
    );
  } catch {
    return null;
  }
};

// Helper function to detect if a message is an Intake Agent form submission
const isIntakeAgentFormData = (message: string): boolean => {
  try {
    const parsed = JSON.parse(message);
    return (
      parsed &&
      typeof parsed === "object" &&
      parsed.candidate_id &&
      parsed.role &&
      parsed.job_family
    );
  } catch {
    return false;
  }
};

// Helper function to format Intake Agent form data for display
const formatIntakeAgentFormData = (message: string): JSX.Element | null => {
  try {
    const parsed = JSON.parse(message);
    if (!parsed || !parsed.candidate_id) return null;

    const labelMap: Record<string, string> = {
      candidate_id: "Candidate ID",
      role: "Role",
      job_family: "Job Family",
      language: "Language",
      role_context: "Role Context",
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: "14px", mb: 0.5 }}>
          Interview Pack Request Submitted
        </Typography>
        {Object.entries(parsed).map(([key, value]) => {
          const label = labelMap[key] || key;

          // Skip empty values or file_metadata
          if (
            !value ||
            value === "" ||
            key === "file_metadata" ||
            (Array.isArray(value) && value.length === 0)
          ) {
            return null;
          }

          // Format the display value
          let displayValue: string;
          if (Array.isArray(value)) {
            displayValue = value.join(", ");
          } else {
            displayValue = String(value);
          }

          return (
            <Box key={key} sx={{ fontSize: "13px", lineHeight: 1.6 }}>
              <strong>{label}:</strong> {displayValue}
            </Box>
          );
        })}
      </Box>
    );
  } catch {
    return null;
  }
};

const MessageBubble: React.FC<MessageBubbleProps> = ({
  // messages,
  currentChat,
  userAvatar,
  agentAvatar,
  sessionId,
  showThinkingBubble = false,
  inputRef,
  agentId,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cotTimeline, setCotTimeline] = useState<TimelineStep[]>(timelineData);
  const [isMouCot, setIsMouCot] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [feedbackPopupState, setFeedbackPopupState] = useState<{
    messageId: string | null;
    anchorEl: HTMLElement | null;
  }>({ messageId: null, anchorEl: null });
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const actionRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const dispatch = useAppDispatch();

  const { loading, isStreaming, streamProgress } = useSelector(
    (state: RootState) => state.user,
  );

  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );

  // Get numeric agent ID from currentAgent (loaded from URL)
  const numericAgentId = currentAgent?.id || 11;

  // console.log("CURRENT CHAT", currentChat);

  // Smooth scroll function with custom easing
  const smoothScrollToBottom = (duration = 400) => {
    if (!chatWindowRef.current) return;

    const start = chatWindowRef.current.scrollTop;
    const end = chatWindowRef.current.scrollHeight;
    const change = end - start;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      if (chatWindowRef.current) {
        chatWindowRef.current.scrollTop = start + change * easeProgress;
      }

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };

  // Auto-scroll immediately when user sends a message
  useEffect(() => {
    smoothScrollToBottom(600); // 600ms duration - slower, more elegant
  }, [currentChat]);

  // Auto-scroll to bottom when agent response completes
  useEffect(() => {
    if (!loading && !isStreaming) {
      smoothScrollToBottom(600); // 600ms duration - slower, more elegant
    }
  }, [loading, isStreaming]);

  // Auto-scroll when stream progress updates (progress tracker changes)
  useEffect(() => {
    if (isStreaming && streamProgress.length > 0) {
      smoothScrollToBottom(400); // Shorter duration for smoother feel during streaming
    }
  }, [streamProgress, isStreaming]);

  // Helper function to get isImageGeneration flag from the most recent user message
  const getIsImageGenerationFlag = (
    currentIndex: number,
  ): boolean | undefined => {
    // Look backwards from current message to find the most recent user message
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (currentChat[i]?.sender === "user") {
        return currentChat[i]?.isImageGeneration;
      }
    }
    return undefined;
  };

  const handleIconClick = async (
    iconId: number,
    messageId: string,
    content: string,
    agentId?: string,
    chatId?: number,
    currentUserPreference?: 0 | 1 | null,
    anchorElement?: HTMLElement,
  ) => {
    console.log("handleIconClick called with:", {
      iconId,
      messageId,
      chatId,
      currentUserPreference,
      anchorElement,
    });

    try {
      if (iconId === 0) {
        // Thumbs up
        const isCurrentlyLiked = currentUserPreference === 1;

        // Call API to save preference if chatId exists and user is liking (not unliking)
        if (chatId && !isCurrentlyLiked) {
          console.log("Sending like preference for chatId:", chatId);

          try {
            await dispatch(
              sendUserPreferenceToAgent({
                chatId,
                userPreference: 1,
                messageId,
              }),
            ).unwrap();
          } catch (error) {
            console.error("API error:", error);
          }

          // Show feedback popup after like attempt
          if (anchorElement) {
            console.log("Setting popup state with anchor:", anchorElement);
            setFeedbackPopupState({
              messageId,
              anchorEl: anchorElement,
            });
          } else {
            console.log("No anchor element found");
          }
        }
      } else if (iconId === 1) {
        // Thumbs down
        const isCurrentlyDisliked = currentUserPreference === 0;

        // Call API to save preference if chatId exists and user is disliking (not undisliking)
        if (chatId && !isCurrentlyDisliked) {
          console.log("Sending dislike preference for chatId:", chatId);

          try {
            await dispatch(
              sendUserPreferenceToAgent({
                chatId,
                userPreference: 0,
                messageId,
              }),
            ).unwrap();
          } catch (error) {
            console.error("API error:", error);
          }

          // Show feedback popup after dislike attempt
          if (anchorElement) {
            console.log("Setting popup state with anchor:", anchorElement);
            setFeedbackPopupState({
              messageId,
              anchorEl: anchorElement,
            });
          } else {
            console.log("No anchor element found");
          }
        }
      } else if (iconId === 3) {
        // Copy functionality
        const success = await copyAgentContent(content, agentId);
        if (success) {
          setCopiedMessageId(messageId);
        }
      } else if (iconId === 2) {
        // Download as PDF functionality
        const pdf = new jsPDF();
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 20;
        const maxWidth = pageWidth - 2 * margin;
        const lineHeight = 7;

        // Check if agent has custom header, otherwise format agent name
        const customHeader = getCustomPDFHeader(content, agentId);

        // Only add header if not redrafter_orchestrator (content-only PDF)
        if (agentId !== "redrafter_orchestrator") {
          const headerText =
            customHeader ||
            (agentId
              ? agentId
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase())
              : "Agent");
          addPDFHeader(pdf, headerText, margin, pageWidth);
        }

        // Format content based on agent type
        const formattedText = formatContentForPDF(content, agentId);

        // Check if content contains Arabic characters
        const containsArabic = hasArabicCharacters(formattedText);

        // Agents that should use canvas-based rendering for proper text direction
        const isLocalizerAgent =
          agentId === "LocalizerAgent" || agentId === "localizer_agent";
        const isSocialMediaAgent = agentId === "social_media_insights_agent";
        const isTranslationAgent = agentId === "translation_agent";

        // Generate custom file name based on agent type
        const fileName = generatePDFFileName(content, agentId);

        // Record download action - fire and forget (non-blocking)
        dispatch(
          recordAgentAction({
            agentId: numericAgentId,
            actionType: "download",
            content: JSON.stringify(formattedText),
          }),
        );

        // Use canvas-based rendering for Arabic content or mixed content agents
        if (
          isLocalizerAgent ||
          (isSocialMediaAgent && containsArabic) ||
          (isTranslationAgent && containsArabic)
        ) {
          generateMixedContentPDF(
            pdf,
            formattedText,
            margin,
            pageWidth,
            pageHeight,
            agentId || "agent",
          )
            .then(() => {
              pdf.save(fileName);
            })
            .catch((error) => {
              console.error("Error rendering mixed content:", error);
              // Fallback to text-based PDF
              generateTextPDF(
                pdf,
                formattedText,
                margin,
                maxWidth,
                pageHeight,
                lineHeight,
                agentId,
              );
              pdf.save(fileName);
            });
        } else {
          // For all other agents, use text-based PDF
          generateTextPDF(
            pdf,
            formattedText,
            margin,
            maxWidth,
            pageHeight,
            lineHeight,
            agentId,
          );
          pdf.save(fileName);
        }
      }
      // Add other icon functionalities here as needed
    } catch (error) {
      console.error("Error in handleIconClick:", error);
      // Don't break UI on error
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className={styles.chatWindow} ref={chatWindowRef}>
      <div className={styles.messageContainer} ref={messageContainerRef}>
        {/* Chat messages will be rendered here */}
        {currentChat
          ?.map((msg, index, array) => {
            // Hide ALL user messages for RFQ Builder (agent ID 45)
            if (msg.sender === "user" && agentId === "45") {
              return null; // Hide all user messages in RFQ Builder
            }

            // Hide RFQ user answer messages completely for other agents
            if (msg.sender === "user") {
              try {
                const parsed = JSON.parse(msg.message);
                // Hide ALL RFQ actions - they're shown in the RFQ form
                if (
                  parsed.action === "provide_answer" ||
                  parsed.action === "submit_answers" ||
                  parsed.action === "confirm_data"
                ) {
                  return null; // Will be filtered out
                }
                // Learning Path form submissions are now shown as user messages
              } catch (e) {
                // Not JSON, keep as is
              }
            }
            return msg;
          })
          .filter((msg) => msg !== null) // Remove hidden messages
          .filter((msg, index, array) => {
            // For RFQ Builder dynamic questions, only show the LATEST message
            if (msg.sender === "agent" && msg.agentIdName === "rfq_builder") {
              // Find all RFQ Builder messages
              const rfqMessages = array.filter(
                (m) => m.sender === "agent" && m.agentIdName === "rfq_builder",
              );
              // Only show if this is the last RFQ message
              return msg.id === rfqMessages[rfqMessages.length - 1]?.id;
            }

            // Hide utility_agent messages if there's an RFQ Builder message in the conversation
            // utility_agent is just a routing agent, not the final response
            if (msg.sender === "agent" && msg.agentIdName === "utility_agent") {
              const hasRfqBuilder = array.some(
                (m) => m.sender === "agent" && m.agentIdName === "rfq_builder",
              );
              if (hasRfqBuilder) {
                return false; // Hide utility agent when RFQ Builder is present
              }
            }

            return true;
          })
          .map((msg, index) => {
            // Filter icons based on agent type
            const visibleIcons =
              msg.agentIdName &&
              downloadExcludedAgents.includes(msg.agentIdName)
                ? iconsData.filter((icon) => icon.id !== 2) // Exclude download icon (id: 2)
                : iconsData; // Show all icons

            // Special rendering for RFQ Builder - full width, no bubble
            if (msg.sender === "agent" && msg.agentIdName === "rfq_builder") {
              return (
                <div key={msg.id} className={styles.rfqBuilderFullWidth}>
                  <AgentResponse
                    content={msg.message}
                    agentId={msg.agentIdName}
                    inputRef={inputRef}
                    currentChat={currentChat}
                    messageId={msg.id}
                  />
                </div>
              );
            }

            return (
              <div
                key={msg.id}
                className={clsx(
                  styles.messageBubble,
                  msg.sender === "user" && styles.userBubble,
                )}
              >
                {msg.sender === "agent" && agentAvatar && (
                  <div className={styles.avatar}>
                    <img
                      src={agentAvatar}
                      alt="Agent avatar"
                      height={24}
                      aria-label="Agent avatar"
                      role="img"
                    />
                  </div>
                )}
                <div style={{ width: "100%" }}>
                  <div
                    className={clsx(
                      styles.message,
                      msg.sender === "user" && styles.userMessage,
                    )}
                  >
                    {msg.sender !== "agent" ? (
                      <div>
                        {(() => {
                          // Check if this is a Document Feedback submission
                          if (isDocumentFeedbackData(msg.message)) {
                            return formatDocumentFeedbackData(msg.message);
                          }

                          // Check if this is a Learning Path form submission
                          if (isLearningPathFormData(msg.message)) {
                            return formatLearningPathFormData(msg.message);
                          }

                          // Check if this is a Training Search form submission
                          if (isTrainingSearchFormData(msg.message)) {
                            return formatTrainingSearchFormData(msg.message);
                          }

                          // Check if this is a Manpower Cost Calculator form submission
                          if (isManpowerCostFormData(msg.message)) {
                            return formatManpowerCostFormData(msg.message);
                          }

                          // Check if this is a Supplier Discovery confirmation
                          if (isSupplierDiscoveryData(msg.message)) {
                            return formatSupplierDiscoveryData(msg.message);
                          }

                          // Check if this is an Intake Agent form submission
                          if (isIntakeAgentFormData(msg.message)) {
                            return formatIntakeAgentFormData(msg.message);
                          }

                          // Extract file information from content for historical messages
                          const { hasFile, fileName, cleanedContent } =
                            extractFileFromContent(msg.message);

                          return (
                            <>
                              {/* Show cleaned content (without full file text) */}
                              {cleanedContent && !hasFile && (
                                <div>{cleanedContent}</div>
                              )}

                              {/* Show file attachment if detected from content */}
                              {hasFile && fileName && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                    marginTop:
                                      cleanedContent &&
                                      cleanedContent !==
                                        `Uploaded file: ${fileName}`
                                        ? "8px"
                                        : "0",
                                  }}
                                >
                                  <Chip
                                    icon={<AttachFileIcon />}
                                    label={fileName}
                                    size="small"
                                    variant="outlined"
                                  />
                                </div>
                              )}

                              {/* Show files array (for live chat mode) */}
                              {msg.files && msg.files.length > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "8px",
                                    marginTop: "8px",
                                  }}
                                >
                                  {msg.files.map((file, index) => (
                                    <Chip
                                      key={index}
                                      icon={<AttachFileIcon />}
                                      label={`${file.name} (${formatFileSize(file.size)})`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <div>
                        {/* Call helper function directly - Hide progress tracker if message contains a question without substantial response (only for contract reviewer) */}
                        {msg.agentIdName &&
                          !progressExcludedAgents.includes(msg.agentIdName) &&
                          !(
                            msg.agentIdName ===
                              "contract_reviewer_orchestrator" &&
                            messageHasQuestionOnly(msg.message)
                          ) && (
                            <StaticProgressTracker
                              steps={getAgentSteps(
                                msg.agentIdName,
                                getIsImageGenerationFlag(index),
                                msg.message,
                              )}
                            />
                          )}
                        <div
                          className={clsx(
                            "message-text-wrapper",
                            (msg.agentIdName === "LocalizerAgent" ||
                              msg.agentIdName === "localizer_agent_final") &&
                              "rtl-text",
                          )}
                        >
                          {/* {console.log("🎯 RENDERING AGENT MESSAGE:", {
                            messageId: msg.id,
                            agentIdName: msg.agentIdName,
                            messageLength: msg.message?.length,
                            messagePreview: msg.message?.substring(0, 50),
                            workflowId: msg.workflowId,
                            requestId: msg.requestId,
                          })} */}
                          <div className={styles.agentUI}>
                            <AgentResponse
                              content={msg.message}
                              agentId={msg.agentIdName}
                              inputRef={inputRef}
                              currentChat={currentChat}
                              messageId={msg.id}
                            />
                          </div>
                          {/* <MarkdownRenderer content={msg.message.toString()} /> */}
                        </div>
                      </div>
                    )}
                  </div>
                  {msg.sender === "agent" &&
                    msg.agentIdName &&
                    !feedbackExcludedAgents.includes(msg.agentIdName) && (
                      <div className={styles.chatActions}>
                        {/* Chain of thought button — MoU agents only */}
                        {MOU_AGENT_IDS.includes(msg.agentIdName ?? "") && (
                          <Tooltip title="Chain of thought">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setCotTimeline(
                                  getMouChainOfThought(msg.message),
                                );
                                setIsMouCot(true);
                                setDrawerOpen(true);
                              }}
                              sx={{ p: "3px" }}
                            >
                              <PsychologyIcon
                                sx={{ fontSize: 20, color: "#6b7280" }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Preview button — MoU agents only, shown when DOCX URL is available */}
                        {MOU_AGENT_IDS.includes(msg.agentIdName ?? "") &&
                          (() => {
                            const docxUrl = extractMouDocxUrl(msg.message);
                            if (!docxUrl) return null;
                            return (
                              <Tooltip title="Preview Document">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const viewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(docxUrl)}`;
                                    window.open(viewerUrl, "_blank");
                                  }}
                                  sx={{ p: "3px" }}
                                >
                                  <VisibilityIcon
                                    sx={{ fontSize: 20, color: "#6b7280" }}
                                  />
                                </IconButton>
                              </Tooltip>
                            );
                          })()}
                        {visibleIcons.map((icon) => (
                          <div
                            key={icon.id}
                            ref={(el) => {
                              if (el && (icon.id === 0 || icon.id === 1)) {
                                actionRefsMap.current.set(
                                  `${msg.id}-${icon.id}`,
                                  el,
                                );
                              }
                            }}
                            onClick={() => {
                              const anchorEl = actionRefsMap.current.get(
                                `${msg.id}-${icon.id}`,
                              );
                              handleIconClick(
                                icon.id,
                                msg.id,
                                msg.message,
                                msg.agentIdName,
                                msg.chatId,
                                msg.userPreference,
                                anchorEl,
                              );
                            }}
                            style={{
                              display: "inline-block",
                              cursor: "pointer",
                            }}
                          >
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={styles.svgActionIcon}
                              style={{ cursor: "pointer" }}
                              // title={icon.id === 3 ? (copiedMessageId === msg.id ? "Copied!" : "Copy") : ""}
                            >
                              <rect
                                width="23.1233"
                                height="23.1233"
                                rx={icon.rectRadius || 2.52}
                                fill="white"
                              />
                              <rect
                                x="0.25"
                                y="0.25"
                                width="22.6233"
                                height="22.6233"
                                rx={icon.rectRadius || 4}
                                className={clsx(
                                  styles.svgActionBorder,
                                  ((icon.id === 3 &&
                                    copiedMessageId === msg.id) ||
                                    (icon.id === 0 &&
                                      msg.userPreference === 1) ||
                                    (icon.id === 1 &&
                                      msg.userPreference === 0)) &&
                                    styles.svgActionBorderActive,
                                )}
                                strokeWidth="0.5"
                              />
                              {icon.paths.map((d, idx) => (
                                <path
                                  key={idx}
                                  d={d}
                                  className={clsx(
                                    styles.iconPath,
                                    ((icon.id === 3 &&
                                      copiedMessageId === msg.id) ||
                                      (icon.id === 0 &&
                                        msg.userPreference === 1) ||
                                      (icon.id === 1 &&
                                        msg.userPreference === 0)) &&
                                      styles.iconPathActive,
                                  )}
                                />
                              ))}
                            </svg>
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </div>
            );
          })}
        {/* Show stream progress tracker when streaming */}
        {showThinkingBubble && (loading || isStreaming) && (
          <div className={clsx(styles.messageBubble, styles.agentBubble)}>
            <div className={styles.avatar}>
              <img
                src={agentAvatar}
                alt="Agent avatar"
                height={24}
                aria-label="Agent avatar"
                role="img"
              />
            </div>
            <StreamProgressTracker
              isImageGeneration={getIsImageGenerationFlag(currentChat.length)}
            />
          </div>
        )}
      </div>
      <ThoughtsDrawer
        title="Chain of thoughts"
        drawerOpen={drawerOpen}
        onCloseDrawer={() => {
          setDrawerOpen(false);
          setIsMouCot(false);
        }}
      >
        {isMouCot ? (
          <MouChainOfThoughtView steps={cotTimeline} />
        ) : (
          <AgentTimeline timeline={cotTimeline} />
        )}
      </ThoughtsDrawer>

      {/* Feedback Popup */}
      <Popover
        open={Boolean(feedbackPopupState.anchorEl)}
        anchorEl={feedbackPopupState.anchorEl}
        onClose={() => {
          console.log("Closing feedback popup");
          setFeedbackPopupState({ messageId: null, anchorEl: null });
        }}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              borderRadius: 2,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
              maxWidth: 320,
              minWidth: 280,
              mb: 1,
            },
          },
        }}
      >
        <Box sx={{ position: "relative", p: 2.5 }}>
          <IconButton
            size="small"
            onClick={() => {
              console.log("Close button clicked");
              setFeedbackPopupState({ messageId: null, anchorEl: null });
            }}
            sx={{
              position: "absolute",
              right: 4,
              top: 4,
              color: "grey.500",
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              mt: 2,
              color: "text.primary",
              fontWeight: 500,
              textAlign: "center",
            }}
          >
            Please provide detailed{" "}
            <a
              style={{
                cursor: "pointer",
                color: "#00d2aa",
                textDecoration: "underline",
              }}
              onClick={() => {
                console.log("Provide Feedback button clicked");
                window.open("/feedback", "_blank");
                setFeedbackPopupState({ messageId: null, anchorEl: null });
              }}
            >
              Feedback
            </a>
          </Typography>
        </Box>
      </Popover>
    </div>
  );
};

export default MessageBubble;
